import { useEffect, useRef, useState } from "react";
import type { Account, Contact, Conversation, DisappearTimerSec } from "@veto/domain";
import { DISAPPEAR_OPTIONS } from "@veto/domain";
import type { ChatRowAction } from "../hooks/useConversationChrome";
import { useLongPress } from "../hooks/useLongPress";
import { useProfileMediaUrl } from "../profile/profileMedia";
import {
  Avatar,
  IconArchive,
  IconBlock,
  IconDetails,
  IconEye,
  IconGroup,
  IconLock,
  IconMoon,
  IconMute,
  IconPin,
  IconPlus,
  IconSearch,
  IconSettings,
  IconShield,
  IconSun,
  IconTimer,
  IconTrash,
} from "@veto/ui";
import { ModalShell } from "./ModalShell";
import { preferAddContactTab } from "./AddContactModal";
import { setTheme, getPreferredTheme, THEME_EVENT } from "../theme";
import { loadNetPrefs } from "../netPrefs";
import { getMeta, loadDraft } from "@veto/storage";

function formatListTime(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/** Renders a contact's received avatar media when present; initials otherwise. */
function ContactAvatar({
  contact,
  title,
  verified,
}: {
  contact: Contact | null | undefined;
  title: string;
  verified?: boolean;
}) {
  const url = useProfileMediaUrl(contact?.avatar ?? null);
  return (
    <Avatar
      name={title}
      verified={verified}
      media={url && contact?.avatar ? { url, kind: contact.avatar.kind } : null}
    />
  );
}

export function ConversationListPane({
  account,
  relayStatus,
  query,
  setQuery,
  conversations,
  filtered,
  contacts,
  activeId,
  onOpen,
  onSettings,
  onOpenProfile,
  onAddContact,
  onNewGroup,
  onChatRowAction,
  onChatLockedCheck,
  onChatDisappearGet,
  onOpenDetails,
  searchMessages,
  onOpenAtMessage,
}: {
  /** Search decrypted messages on this device (phone parity). */
  searchMessages?: (q: string) => Promise<MessageHit[]>;
  onOpenAtMessage?: (conversationId: string, q: string) => void;
  account: Account | null;
  relayStatus: string;
  query: string;
  setQuery: (v: string) => void;
  conversations: Conversation[];
  filtered: Conversation[];
  contacts: Contact[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onSettings: () => void;
  onOpenProfile: () => void;
  onAddContact: () => void;
  onNewGroup: () => void;
  onChatRowAction: (
    conv: Conversation,
    contact: Contact | null,
    action: ChatRowAction,
    value?: DisappearTimerSec,
  ) => void;
  onChatLockedCheck: (conversationId: string) => Promise<boolean>;
  onChatDisappearGet: (conversationId: string) => Promise<DisappearTimerSec>;
  onOpenDetails: (id: string) => void;
}) {
  const statusClass =
    relayStatus === "connected" ? "online" : relayStatus === "degraded" ? "degraded" : "offline";
  const prefs = loadNetPrefs();
  const myAvatarUrl = useProfileMediaUrl(account?.avatar ?? null);
  const [theme, setThemeState] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.dataset.theme === "light"
        ? "light"
        : "dark"
      : getPreferredTheme(),
  );

  // Keep the top-bar toggle in sync when theme changes elsewhere (Settings → Appearance).
  useEffect(() => {
    const onTheme = (e: Event) => setThemeState((e as CustomEvent<"dark" | "light">).detail);
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  // Long-press (touch) / right-click (desktop) chat menu. `pending` records which
  // row was pressed; the sheet opens once lock + timer state resolve (async meta reads).
  const [menu, setMenu] = useState<{
    convId: string;
    locked: boolean;
    disappear: DisappearTimerSec;
  } | null>(null);
  // Archived folder — archive used to strand chats; this is the only way back.
  const [showArchived, setShowArchived] = useState(false);
  const [messageHits, setMessageHits] = useState<MessageHit[]>([]);
  // Unsent drafts show in the list as "Draft: …" (phone parity).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    void Promise.all(
      conversations.map(async (c) => [c.id, (await loadDraft(c.id))?.text?.trim() ?? ""] as const),
    ).then((rows) => {
      if (live) setDrafts(Object.fromEntries(rows.filter(([, text]) => text)));
    });
    return () => {
      live = false;
    };
  }, [conversations, activeId]);
  useEffect(() => {
    const term = query.trim();
    if (!searchMessages || term.length < 2) {
      setMessageHits([]);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      void searchMessages(term).then((hits) => live && setMessageHits(hits));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query, searchMessages]);

  // Locked chats (phone parity): out of the list, search and archive; behind one row, and even
  // there they show no name or message — each still opens only with its PIN.
  const [showLocked, setShowLocked] = useState(false);
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let live = true;
    void Promise.all(
      conversations.map(async (c) => ((await getMeta(`chatlock.${c.id}`)) ? c.id : null)),
    ).then((ids) => {
      if (!live) return;
      const next = new Set(ids.filter((x): x is string => !!x));
      setLockedIds((prev) =>
        prev.size === next.size && [...next].every((x) => prev.has(x)) ? prev : next,
      );
      if (next.size === 0) setShowLocked(false);
    });
    return () => {
      live = false;
    };
  }, [conversations]);
  const lp = useLongPress();

  // Swipe-to-archive (touch/pen): horizontal drag previews a row shift; release
  // past the threshold toggles archive. Styles are set on the DOM node directly
  // so the 60 fps drag loop never triggers a React re-render. Vertical intent
  // (scrolling) is left to the browser via `touch-action: pan-y`.
  const swipeRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    dx: number;
    active: boolean;
  } | null>(null);
  const swipedRef = useRef(false);

  function onRowSwipeStart(e: React.PointerEvent, id: string) {
    if (e.pointerType === "mouse") return;
    swipedRef.current = false;
    swipeRef.current = { id, startX: e.clientX, startY: e.clientY, dx: 0, active: false };
  }

  function onRowSwipeMove(e: React.PointerEvent, id: string) {
    const s = swipeRef.current;
    if (!s || s.id !== id) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.active) {
      // Activate only on clear horizontal intent; otherwise let the page scroll.
      if (Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        s.active = true;
        swipedRef.current = true;
      } else {
        return;
      }
    }
    s.dx = Math.max(-110, Math.min(110, dx));
    const el = e.currentTarget as HTMLElement;
    el.style.transition = "none";
    el.style.transform = `translateX(${s.dx}px)`;
  }

  function onRowSwipeSettle(el: HTMLElement, c: Conversation) {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s?.active) return;
    el.style.transition = "transform 0.16s ease";
    el.style.transform = "";
    window.setTimeout(() => {
      el.style.transition = "";
    }, 180);
    if (Math.abs(s.dx) > 64) {
      const contact = contacts.find((x) => x.id === c.contactId) ?? null;
      onChatRowAction(c, contact, "archive");
    }
  }

  function onRowSwipeEnd(e: React.PointerEvent, c: Conversation) {
    onRowSwipeSettle(e.currentTarget as HTMLElement, c);
  }

  function onRowSwipeCancel(e: React.PointerEvent) {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s?.active) return;
    const el = e.currentTarget as HTMLElement;
    el.style.transition = "transform 0.16s ease";
    el.style.transform = "";
    window.setTimeout(() => {
      el.style.transition = "";
    }, 180);
  }

  async function openMenu(c: Conversation) {
    const [locked, disappear] = await Promise.all([
      onChatLockedCheck(c.id),
      onChatDisappearGet(c.id),
    ]);
    setMenu({ convId: c.id, locked, disappear });
  }

  const menuConv = menu ? (conversations.find((c) => c.id === menu.convId) ?? null) : null;
  const menuContact = menuConv ? (contacts.find((x) => x.id === menuConv.contactId) ?? null) : null;

  function rowAction(action: ChatRowAction, value?: DisappearTimerSec) {
    if (!menuConv) return;
    onChatRowAction(menuConv, menuContact, action, value);
  }

  const q = query.trim().toLowerCase();
  const matchesQuery = (c: Conversation) =>
    !q || c.title.toLowerCase().includes(q) || c.lastMessagePreview.toLowerCase().includes(q);
  const archivedAll = conversations.filter((c) => c.archived && !lockedIds.has(c.id));
  const archivedList = archivedAll.filter(matchesQuery);
  const lockedAll = conversations.filter((c) => lockedIds.has(c.id));
  const rows = showLocked
    ? lockedAll
    : showArchived
      ? archivedList
      : filtered.filter((c) => !lockedIds.has(c.id));

  /** A locked chat says nothing about itself in the list: no name, no message, no avatar. */
  function renderLockedRow(c: Conversation) {
    return (
      <button
        key={c.id}
        className={["conv-item", "conv-locked", c.id === activeId ? "active" : ""]
          .filter(Boolean)
          .join(" ")}
        onClick={() => onOpen(c.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          void openMenu(c);
        }}
        role="listitem"
        aria-current={c.id === activeId ? "true" : undefined}
        aria-label="Locked chat — opens with its PIN"
      >
        <span className="conv-locked-avatar" aria-hidden>
          <IconLock size={20} />
        </span>
        <div className="conv-meta">
          <div className="conv-row">
            <span className="conv-title">
              <strong>Locked chat</strong>
            </span>
          </div>
          <div className="conv-row">
            <span className="preview">Opens with its PIN</span>
          </div>
        </div>
      </button>
    );
  }

  function renderRow(c: Conversation) {
    if (lockedIds.has(c.id)) return renderLockedRow(c);
    const contact = contacts.find((x) => x.id === c.contactId);
    const hasUnread = c.unread > 0;
    const attention = hasUnread && !c.muted;
    const preview = c.lastMessagePreview?.trim() || "No messages yet";
    return (
      <button
        key={c.id}
        className={[
          "conv-item",
          c.id === activeId ? "active" : "",
          attention ? "has-unread" : "",
          hasUnread && c.muted ? "has-unread-muted" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => {
          // A long-press already opened the menu, or a swipe just ended —
          // swallow the trailing click.
          const swallowed = lp.consumeFired() || swipedRef.current;
          swipedRef.current = false;
          if (swallowed) return;
          onOpen(c.id);
        }}
        onPointerDown={(e) => {
          onRowSwipeStart(e, c.id);
          lp.begin(e, () => void openMenu(c));
        }}
        onPointerMove={(e) => {
          onRowSwipeMove(e, c.id);
          lp.move(e);
        }}
        onPointerUp={(e) => {
          lp.end();
          onRowSwipeEnd(e, c);
        }}
        onPointerCancel={(e) => {
          lp.end();
          onRowSwipeCancel(e);
        }}
        onPointerLeave={(e) => {
          lp.end();
          onRowSwipeCancel(e);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          lp.cancel();
          void openMenu(c);
        }}
        role="listitem"
        aria-current={c.id === activeId ? "true" : undefined}
        aria-label={`${c.title}${c.requestPending ? ", message request" : ""}${
          hasUnread ? `, ${c.unread} unread${c.muted ? ", muted" : ""}` : ""
        }, ${preview}`}
      >
        <ContactAvatar
          contact={contact}
          title={c.title}
          verified={contact?.verificationState === "verified"}
        />
        <div className="conv-meta">
          <div className="conv-row">
            <span className="conv-title">
              <strong className={attention ? "unread-title" : undefined}>{c.title}</strong>
              <span className="conv-flags">
                {c.pinned ? (
                  <span title="Pinned" aria-label="Pinned">
                    <IconPin />
                  </span>
                ) : null}
                {c.muted ? (
                  <span title="Muted" aria-label="Muted">
                    <IconMute />
                  </span>
                ) : null}
                {contact?.blockedAt ? (
                  <span title="Blocked" aria-label="Blocked">
                    <IconBlock />
                  </span>
                ) : null}
                {c.requestPending || contact?.requestState === "pending" ? (
                  <span
                    className="conv-request-chip"
                    title="Not in your contacts — open to accept or block"
                  >
                    Request
                  </span>
                ) : null}
              </span>
            </span>
            <span className={`conv-time${attention ? " attention" : ""}`}>
              {formatListTime(c.lastMessageAt)}
            </span>
          </div>
          <span className={`preview${attention ? " attention" : hasUnread ? " muted-unread" : ""}`}>
            {drafts[c.id] && c.id !== activeId ? (
              <>
                <span className="draft-label">Draft:</span> {drafts[c.id]}
              </>
            ) : (
              preview
            )}
          </span>
        </div>
        {hasUnread && (
          <span
            className={`unread${c.muted ? " muted" : ""}`}
            aria-hidden
            title={c.muted ? "Muted · unread" : `${c.unread} unread`}
          >
            {c.unread > 99 ? "99+" : c.unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <section className="pane pane-list">
      <header className="topbar">
        <div className="brand" aria-label="Veto">
          <span className="brand-mark">V</span>
          <span className="brand-wordmark">
            <em>V</em>eto
          </span>
        </div>
        <div className="grow" />
        <span
          className={`status-pill ${statusClass}`}
          role="status"
          aria-label={`Relays ${relayStatus} · public mesh ${prefs.publicMesh ? "on" : "off"} · peer mesh ${prefs.peerMesh ? "on" : "off"}`}
          title={`Relays ${relayStatus} · public mesh ${prefs.publicMesh ? "on" : "off"} · peer mesh ${prefs.peerMesh ? "on" : "off"} — messages are always end-to-end encrypted`}
        >
          <span className="dot" aria-hidden="true" />
        </span>
        <button
          type="button"
          className="icon-btn theme-toggle"
          aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
          title={theme === "light" ? "Switch to dark" : "Switch to light"}
          onClick={() => {
            const next = theme === "light" ? "dark" : "light";
            setThemeState(next);
            setTheme(next);
          }}
        >
          {theme === "light" ? <IconMoon size={19} /> : <IconSun size={19} />}
        </button>
        <button
          className="icon-btn avatar-topbar-btn"
          aria-label="My profile"
          title="My profile"
          onClick={onOpenProfile}
        >
          <Avatar
            name={account?.displayName ?? "Veto user"}
            size={30}
            media={
              myAvatarUrl && account?.avatar
                ? { url: myAvatarUrl, kind: account.avatar.kind }
                : null
            }
          />
        </button>
        <button className="icon-btn" aria-label="Settings" title="Settings" onClick={onSettings}>
          <IconSettings />
        </button>
        <button className="icon-btn" aria-label="New group" title="New group" onClick={onNewGroup}>
          <IconGroup />
        </button>
        <button
          className="icon-btn"
          aria-label="Add contact"
          title="Add contact"
          onClick={onAddContact}
        >
          <IconPlus />
        </button>
      </header>
      <div className="search-wrap">
        <label className="search-field">
          <IconSearch />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations"
            aria-label="Search conversations"
          />
        </label>
      </div>
      <div className="list" role="list">
        {lockedAll.length > 0 && !showArchived && (
          <button
            type="button"
            className="archived-toggle locked-toggle"
            aria-expanded={showLocked}
            onClick={() => setShowLocked((v) => !v)}
          >
            <IconLock size={18} />
            <span>{showLocked ? "Back to chats" : "Locked chats"}</span>
            <span className="archived-count">{lockedAll.length}</span>
          </button>
        )}
        {archivedAll.length > 0 && !showLocked && (
          <button
            type="button"
            className="archived-toggle"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            <IconArchive size={18} />
            <span>{showArchived ? "Back to chats" : "Archived"}</span>
            <span className="archived-count">{archivedAll.length}</span>
          </button>
        )}
        {rows.length > 0 && (
          <div className="list-head" aria-hidden>
            <h2>{showLocked ? "Locked chats" : showArchived ? "Archived chats" : "Chats"}</h2>
            <span className="list-count">{rows.length}</span>
          </div>
        )}
        {rows.length === 0 && !showArchived && !showLocked && messageHits.length === 0 && (
          <div className="empty" role="status">
            <div className="empty-card">
              <div className="empty-illus">
                <IconShield size={32} />
              </div>
              <h2>{conversations.length === 0 ? "No chats yet" : "No matching chats"}</h2>
              <p className="muted">
                {conversations.length === 0
                  ? "Send your invite link to a friend, or paste theirs. No phone number, no sign-up."
                  : `Nothing matches “${query.trim()}”. Clear search or try another name.`}
              </p>
              {conversations.length === 0 ? (
                <div className="empty-actions">
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      preferAddContactTab("share");
                      onAddContact();
                    }}
                  >
                    Invite a friend
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => {
                      preferAddContactTab("add");
                      onAddContact();
                    }}
                  >
                    I have an invite
                  </button>
                </div>
              ) : (
                <button className="btn secondary" type="button" onClick={() => setQuery("")}>
                  Clear search
                </button>
              )}
            </div>
          </div>
        )}
        {rows.length === 0 && showArchived && (
          <div className="empty" role="status">
            <div className="empty-card">
              <div className="empty-illus">
                <IconArchive size={32} />
              </div>
              <h2>{q ? "No matching archived chats" : "No archived chats"}</h2>
              <p className="muted">
                {q
                  ? `Nothing archived matches “${query.trim()}”.`
                  : "Long-press a chat (or right-click) and choose Archive to tuck it away here."}
              </p>
            </div>
          </div>
        )}
        {rows.map(renderRow)}
        {!showLocked && !showArchived && messageHits.length > 0 && (
          <>
            <div className="list-head" aria-hidden>
              <h2>Messages</h2>
              <span className="list-count">{messageHits.length}</span>
            </div>
            {messageHits.map((h) => (
              <button
                key={`${h.conversationId}-${h.eventId}`}
                type="button"
                className="conv-item message-hit"
                role="listitem"
                onClick={() => onOpenAtMessage?.(h.conversationId, query.trim())}
              >
                <div className="conv-meta">
                  <div className="conv-row">
                    <span className="conv-title">
                      <strong>{h.title}</strong>
                    </span>
                    <span className="conv-time muted">
                      {new Date(h.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="conv-row">
                    <span className="preview">{snippet(h.body, query.trim())}</span>
                  </div>
                </div>
              </button>
            ))}
          </>
        )}
      </div>
      <button
        type="button"
        className="fab"
        aria-label="Start new chat"
        title="Start new chat"
        onClick={() => {
          preferAddContactTab("add");
          onAddContact();
        }}
      >
        <IconPlus />
      </button>

      {menuConv && menu && (
        <ModalShell
          title={menuConv.title}
          ariaLabel={`Actions for chat ${menuConv.title}`}
          onClose={() => setMenu(null)}
          size="sheet"
          className="chat-menu-sheet"
        >
          <div className="chat-menu-list">
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                setMenu(null);
                onOpenDetails(menuConv.id);
              }}
            >
              <IconDetails size={18} />
              <span>Chat details</span>
            </button>
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("read");
                setMenu(null);
              }}
            >
              <IconEye size={18} />
              <span>{menuConv.unread > 0 ? "Mark as read" : "Mark as unread"}</span>
            </button>
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("pin");
                setMenu(null);
              }}
            >
              <IconPin size={18} />
              <span>{menuConv.pinned ? "Unpin chat" : "Pin chat"}</span>
            </button>
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("mute");
                setMenu(null);
              }}
            >
              <IconMute size={18} />
              <span>{menuConv.muted ? "Unmute notifications" : "Mute notifications"}</span>
            </button>
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("archive");
                setMenu(null);
              }}
            >
              <IconArchive size={18} />
              <span>{menuConv.archived ? "Unarchive" : "Archive"}</span>
            </button>
            {menuConv.type === "direct" && (
              <div className="chat-menu-row chat-menu-static">
                <IconTimer size={18} />
                <label htmlFor="chatMenuDisappear">Disappearing messages</label>
                <select
                  id="chatMenuDisappear"
                  value={menu.disappear}
                  onChange={(e) => {
                    const sec = Number(e.target.value) as DisappearTimerSec;
                    rowAction("disappear", sec);
                    setMenu({ ...menu, disappear: sec });
                  }}
                >
                  {DISAPPEAR_OPTIONS.map((o) => (
                    <option key={o.sec} value={o.sec}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("chat-lock");
                setMenu(null);
              }}
            >
              <IconLock size={18} />
              <span>{menu.locked ? "Unlock chat" : "Lock chat"}</span>
            </button>
            {menuConv.type === "direct" &&
              menuContact &&
              menuContact.verificationState !== "verified" && (
                <button
                  type="button"
                  className="chat-menu-row"
                  onClick={() => {
                    setMenu(null);
                    onOpenDetails(menuConv.id);
                  }}
                >
                  <IconShield size={18} />
                  <span>Verify safety number</span>
                </button>
              )}
            {menuContact && (
              <button
                type="button"
                className="chat-menu-row danger"
                onClick={() => {
                  rowAction("block");
                  setMenu(null);
                }}
              >
                <IconBlock size={18} />
                <span>{menuContact.blockedAt ? "Unblock" : "Block"}</span>
              </button>
            )}
            <button
              type="button"
              className="chat-menu-row"
              onClick={() => {
                rowAction("clear");
                setMenu(null);
              }}
            >
              <IconTrash size={18} />
              <span>Clear chat</span>
            </button>
            <button
              type="button"
              className="chat-menu-row danger"
              onClick={() => {
                rowAction("delete");
                setMenu(null);
              }}
            >
              <IconTrash size={18} />
              <span>{menuConv.type === "group" ? "Leave group" : "Delete chat"}</span>
            </button>
          </div>
          <p className="muted text-sm chat-menu-hint" style={{ margin: 0 }}>
            Long-press any chat (or right-click) for these actions.
          </p>
        </ModalShell>
      )}
    </section>
  );
}

/** One decrypted message matching the list search. */
export interface MessageHit {
  conversationId: string;
  title: string;
  eventId: string;
  body: string;
  createdAt: number;
}

/** A short line around the first match. */
function snippet(body: string, q: string): string {
  const flat = body.replace(/\s+/g, " ");
  const i = flat.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return flat.slice(0, 90);
  const start = Math.max(0, i - 30);
  return `${start > 0 ? "…" : ""}${flat.slice(start, i + q.length + 50)}${i + q.length + 50 < flat.length ? "…" : ""}`;
}
